import { useMemo } from 'react';
import { CgFileDocument } from 'react-icons/cg';
import { FaRegEdit } from 'react-icons/fa';
import { MdContactSupport } from 'react-icons/md';

import { DataContractYAML } from '@/types/dataContracts';
import envVarsProvider from '@/utils/envVars/envVars';

import { classifyContractError } from '../utils/classifyContractError';

interface TipAction {
  label: string;
  icon: React.ComponentType<any> | null;
  onClick: () => void;
}

export interface Tip {
  title: string;
  description: string;
  actions?: TipAction[];
}

interface UseTroubleshootingTipsOptions {
  errorMessage: string | null | undefined;
  taskType: string | null | undefined;
  parsedYAML: DataContractYAML | null;
  onShowYAML: () => void;
  onUpdateContract: () => void;
  onShowProcessingModal: () => void;
}

export function useTroubleshootingTips( options: UseTroubleshootingTipsOptions ): Tip[] {
  const { errorMessage, taskType, parsedYAML, onShowYAML, onUpdateContract } = options;

  return useMemo( () => {
    const category = classifyContractError( errorMessage, taskType );

    const contactSupportTip: Tip = buildContactSupportTip();

    const verifyYAMLTip: Tip = {
      title: 'Verify YAML Syntax',
      description: 'Check your YAML file for syntax errors and ensure all required fields are present.',
      actions: [
        {
          label: 'View YAML',
          icon: CgFileDocument,
          onClick: onShowYAML
        },
        {
          label: 'Edit Contract',
          icon: FaRegEdit,
          onClick: onUpdateContract
        }
      ]
    };

    const checkConfigTip: Tip = {
      title: 'Check Configuration',
      description: 'Verify that source and destination server configurations are correct.',
      actions: [
        {
          label: 'Edit Contract',
          icon: FaRegEdit,
          onClick: onUpdateContract
        }
      ]
    };

    const reviewCredentialsTip: Tip = {
      title: 'Review Credentials',
      description: 'Ensure the referenced credentials and connections are active and have proper permissions.',
      actions: [
        {
          label: 'Edit Contract',
          icon: FaRegEdit,
          onClick: onUpdateContract
        }
      ]
    };

    const identifyFailingProviderTip: Tip = {
      title: 'Find the failing provider',
      description: 'Expand "Show full error log" above and look for lines starting with `error:`. The provider name (snowflake, airbyte, etc.) on that line is the system to investigate first.'
    };

    const fixProviderResourceTip: Tip = {
      title: 'Verify credentials and resource names',
      description: 'Confirm the failing provider\'s credentials are active, then check that your contract\'s resource names follow that provider\'s identifier rules (e.g., Snowflake disallows hyphens and leading digits in unquoted names).',
      actions: [
        {
          label: 'View YAML',
          icon: CgFileDocument,
          onClick: onShowYAML
        },
        {
          label: 'Edit Contract',
          icon: FaRegEdit,
          onClick: onUpdateContract
        }
      ]
    };

    switch ( category ) {
      case 'yaml_error':
        return [ verifyYAMLTip, checkConfigTip, contactSupportTip ];

      case 'access_error':
        return [ reviewCredentialsTip, checkConfigTip, contactSupportTip ];

      case 'connection_error':
        return [ checkConfigTip, reviewCredentialsTip, contactSupportTip ];

      case 'infrastructure_error':
        return [ identifyFailingProviderTip, fixProviderResourceTip, contactSupportTip ];

      case 'general_error':
      default:
        return [ verifyYAMLTip, checkConfigTip, reviewCredentialsTip, contactSupportTip ];
    }
  }, [ errorMessage, taskType, parsedYAML, onShowYAML, onUpdateContract ] );
}

function buildContactSupportTip(): Tip {
  return {
    title: 'Contact Support',
    description: 'If the issue persists, contact support with the error details for further assistance.',
    actions: [
      {
        label: 'Contact Us',
        icon: MdContactSupport,
        onClick: () => { window.location.href = `mailto:${ envVarsProvider.get( 'CONTACT_US_EMAIL' ) }`; }
      }
    ]
  };
}
